import { useCallback, useEffect, useRef, useState } from 'react';
import { LESSON_STEPS, LESSON_PRODUCT_ID, LESSON_SCREEN_TARGETS, LESSON_DRAG_TEXT_ID } from './lessonState';

/** Observe the shared editor's state; lesson steps never perform the user's task. */
export default function useLessonGuide({ initialStep, state, t }) {
  const [progress, setStep] = useState(initialStep);
  const dragLesson = progress >= 15;
  const screenLesson = progress >= 12 && !dragLesson;
  const [toolbox, setToolbox] = useState('components');
  const [dragId, setDragId] = useState(initialStep >= 16 ? LESSON_DRAG_TEXT_ID : null);
  const [lastDrop, setLastDrop] = useState(null);
  const build = progress >= 4 && progress < 12;
  const step = build ? progress - 4 : progress;
  const [selectedId, setSelectedId] = useState(null);
  const [navigation, setNavigation] = useState('blocks');
  useEffect(() => { setSelectedId(null); }, [state.currentScreen, state.currentDevice]);
  const originalId = useRef(null);
  const copyId = useRef(null);
  const canvas = state.config.search.views.searchBox.screens.results.canvas;
  const product = canvas.nodes.find(node => node.id === LESSON_PRODUCT_ID);
  const texts = canvas.nodes.filter(node => node.type === 'text');
  if ((build && step === 0) || !originalId.current) originalId.current = texts[0]?.id || null;
  if (!copyId.current && texts.length > 1) copyId.current = texts.find(node => node.id !== originalId.current)?.id;
  const original = canvas.nodes.find(node => node.id === originalId.current);
  const copy = canvas.nodes.find(node => node.id === copyId.current);
  const root = canvas.nodes.find(node => node.id === canvas.root);
  const column1 = root?.slots?.['col-1'] || [];
  const column2 = root?.slots?.['col-2'] || [];
  const wrongScreen = !screenLesson && state.currentScreen !== 'results';
  const missing = !screenLesson && (!product || (build && step > 0 && !original) || (dragLesson && progress > 15 && !canvas.nodes.some(node => node.id === dragId)));
  const complete = progress === LESSON_STEPS.length;
  const dragMatches = lastDrop?.step === progress && lastDrop.nodeId === dragId;
  const canFinish = progress === 17 && !wrongScreen && dragMatches && lastDrop.slotKey === 'col-2'
    && column2.includes(dragId) && column2.indexOf(dragId) < column2.indexOf(product?.id);

  useEffect(() => {
    if (missing || complete || wrongScreen) return;
    let done = false;
    if (dragLesson) {
      done = dragMatches && ((progress === 15 && lastDrop.slotKey === 'col-1' && column1.includes(dragId))
        || (progress === 16 && lastDrop.slotKey === 'col-2' && column2.includes(dragId) && column2.indexOf(dragId) > column2.indexOf(product.id)));
    } else if (screenLesson) {
      done = state.currentScreen === LESSON_SCREEN_TARGETS[progress];
    } else if (build) {
      if (step === 0) done = Boolean(original && column1.includes(original.id) && original.props.text === t('experience.lesson.sampleText'));
      if (step === 2) done = column2.includes(original.id);
      if (step === 3) done = column2.includes(original.id) && column2.indexOf(original.id) < column2.indexOf(product.id);
      if (step === 4) done = Boolean(copy && column2.includes(copy.id));
      if (step === 5) done = Boolean(copy?.props.text === t('experience.lesson.copyText') && original.props.text !== t('experience.lesson.copyText'));
      if (step === 6) done = Boolean(copyId.current && !copy);
      if (step === 7) done = Boolean(texts.length >= 2 && state.editorHistory.future.length);
    } else {
      if (step === 1) done = selectedId === LESSON_PRODUCT_ID && product.props.productsInRow === 3;
      if (step === 2) done = product.props.productsInRow === 4 && state.editorHistory.future.length > 0;
    }
    if (done) setStep(previous => previous + 1);
  }, [state, step, selectedId, missing, complete, build, screenLesson, progress, wrongScreen, dragLesson, dragMatches, lastDrop, dragId]);

  const onInteraction = useCallback(event => {
    if (event.type === 'toolbox') setToolbox(event.value);
    if (event.type === 'drop' && dragLesson && !wrongScreen) {
      if (progress === 15 && event.operation === 'add' && event.componentType === 'text') setDragId(event.nodeId);
      setLastDrop({ ...event, step: progress });
    }
    if (event.type === 'navigation') setNavigation(event.value);
    if (event.type !== 'selection' || wrongScreen || screenLesson || dragLesson) return;
    setSelectedId(event.nodeId);
    if (build) {
      if (event.nodeId === originalId.current && event.source === 'navigation' && event.navigation === 'tree') {
        setStep(previous => previous === 5 ? 6 : previous);
      }
    } else if (event.nodeId === LESSON_PRODUCT_ID) {
      if (event.source === 'preview') setStep(previous => previous === 0 ? 1 : previous);
      if (event.source === 'navigation' && event.navigation === 'tree') setStep(previous => previous === 3 ? 4 : previous);
    }
  }, [build, canvas.root, wrongScreen, screenLesson, dragLesson, progress]);

  const wantedId = build ? (step === 5 || step === 6 ? copyId.current : originalId.current) : LESSON_PRODUCT_ID;
  const needsSelection = build ? step >= 2 && step <= 6 && selectedId !== wantedId : step === 1 && selectedId !== wantedId;
  let instruction = LESSON_STEPS[progress];
  if (!build && step === 3 && navigation === 'tree') instruction = 'findInTree';
  if (build && step === 1 && navigation === 'tree') instruction = 'buildContainer';
  if (build && step === 0 && original) instruction = column1.includes(original.id) ? 'buildName' : 'buildWrongColumn';
  if (needsSelection) instruction = build ? (step >= 5 ? 'buildSelectCopy' : 'buildSelectText') : 'reselect';
  if (dragLesson && progress === 15 && dragId) instruction = 'dragPlace';
  if (wrongScreen) instruction = dragLesson ? 'dragReturnResults' : 'returnResults';
  if (missing) instruction = 'missing';
  if (canFinish) instruction = 'dragDone';

  const resolveTarget = useCallback(element => {
    const contentTab = () => element.querySelector('[data-editor-toolbox="content"]');
    const row = id => [...element.querySelectorAll('[data-outline-node]')].find(node => node.dataset.outlineNode === id)?.querySelector('button[draggable]');
    const button = key => [...element.querySelectorAll('button')].find(node => node.getAttribute('aria-label') === t(key) || node.textContent.trim() === t(key));
    const field = key => [...element.querySelectorAll('input, textarea, [role="slider"]')].find(node => node.getAttribute('aria-label') === t(key));
    if (canFinish) return element.querySelector('[data-lesson-finish]');
    if (wrongScreen || screenLesson) return element.querySelector(`[data-editor-preview-controls] button[value="${wrongScreen ? 'results' : LESSON_SCREEN_TARGETS[progress]}"]`);
    if (dragLesson) {
      if (progress === 15 && !dragId) {
        if (toolbox !== 'components') return element.querySelector('[data-editor-toolbox="components"]');
        const component = element.querySelector('[data-add-component="text"]');
        return component || field('experience.design.searchLibrary');
      }
      return element.querySelector(`[data-preview-node-id="${dragId}"]`);
    }
    if (needsSelection) return toolbox === 'content' ? row(wantedId) : element.querySelector(`[data-preview-node-id="${wantedId}"]`);
    if (build) {
      if (step === 0 && original) {
        if (selectedId !== original.id) return toolbox === 'content' ? row(original.id) : element.querySelector(`[data-preview-node-id="${original.id}"]`);
        return column1.includes(original.id) ? field('searchInterface.inspector.field.text') : button('experience.design.moveTo');
      }
      if (step === 0) return toolbox !== 'components' ? element.querySelector('[data-editor-toolbox="components"]') : element.querySelector('[data-add-component="text"]') || field('experience.design.searchLibrary');
      if (step === 1) return toolbox !== 'content' ? contentTab() : navigation === 'tree' ? row(originalId.current) : element.querySelector('[data-editor-navigation]');
      if (step === 2) return button('experience.design.moveTo');
      if (step === 3) return button('experience.design.moveUp');
      if (step === 4) return button('experience.design.duplicate');
      if (step === 5) return field('searchInterface.inspector.field.text');
      if (step === 6) return button('experience.design.remove');
      if (step === 7) return element.querySelector('[data-lesson-undo]');
    } else {
      if (step === 2) return element.querySelector('[data-lesson-undo]');
      if (step === 3) return toolbox !== 'content' ? contentTab() : navigation === 'tree' ? row(LESSON_PRODUCT_ID) : element.querySelector('[data-editor-navigation]');
      if (step === 1) return field('searchInterface.inspector.field.productsInRow');
      return element.querySelector(`[data-preview-node-id="${LESSON_PRODUCT_ID}"]`);
    }
    return null;
  }, [step, navigation, needsSelection, wantedId, build, canvas.root, original, column1, selectedId, t, wrongScreen, screenLesson, progress, canFinish, dragLesson, dragId, toolbox]);

  const resolveDropTarget = useCallback(element => {
    if (!dragLesson || wrongScreen || canFinish || complete) return null;
    return element.querySelector(progress === 17 ? `[data-preview-node-id="${LESSON_PRODUCT_ID}"]`
      : `[data-preview-slot-owner="${canvas.root}"][data-preview-slot="col-${progress === 15 ? 1 : 2}"]`);
  }, [dragLesson, wrongScreen, canFinish, complete, progress, canvas.root]);

  return { step: progress, complete, missing, instruction, resolveTarget, resolveDropTarget, onInteraction, canFinish,
    finish: () => { if (canFinish) setStep(LESSON_STEPS.length); },
  };
}
